import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { acceptAdById, rejectAdById } from "../api/status";
import getAnnouncements from "../api/getads";
import editAnnoucmentByID from "../api/editAnnoucment";
import Modal from "./Modal";
import getAdminAnnouncementById from "../api/getAdminAnnouncementById";
import getAnnouncementByIdPublic from "../api/getAnnouncementById";
import getCategories from "../api/getCategories";
import { getRegionsPublic } from "../api/locationManagementAPI";
import AdDetailsModalContent from "./AdDetailsModalContent";
import BulkModerationActions from "./BulkModerationActions";
import { bulkAccept, bulkReject, bulkDelete } from "../api/moderation/bulkOperations";

import { parseAnnouncementsListResponse } from "./moderation/utils";
import ModerationHeader from "./moderation/ModerationHeader";
import ModerationRow from "./moderation/ModerationRow";
import ModerationDetailBar from "./moderation/ModerationDetailBar";
import { useToasts, ToastStack } from "./moderation/Toasts";
import Pagination from "./moderation/Pagination";
import EmptyState from "./moderation/EmptyState";

const ITEMS_PER_PAGE = 20;
// «Барлығын таңдау» — барлық беттердің ID-ларын осы өлшеммен жинаймыз.
const SELECT_ALL_PAGE_SIZE = 100;
const ID_QUERY_RE = /^\s*(#|№|id\s*:?)?\s*\d{1,9}\s*$/i;

/** Тізім сұранысының параметрлері (сүзгілер бойынша). */
const buildListParams = (f, pageNum, limit) => {
  const params = new URLSearchParams({ status: f.status, page: pageNum, limit });
  if (f.category) params.append("category_id", f.category);
  if (f.subcategory) params.append("subcategory_id", f.subcategory);
  if (f.region) params.append("region_id", f.region);
  if (f.search) params.append("search", f.search);
  if (f.sortBy) params.append("sort", f.sortBy);
  if (f.photos !== null) params.append("photo_filter", f.photos ? "with" : "without");
  if (f.date) {
    if (f.date.type) {
      params.append("date_type", f.date.type);
    } else {
      if (f.date.from) params.append("date_from", f.date.from);
      if (f.date.to) params.append("date_to", f.date.to);
    }
  }
  return params;
};

export default function Moderation({ status }) {
  const [announcements, setAnnouncements] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Әрекеті жүріп жатқан жарнамалар — қайта басу бұғатталады, жолда спиннер.
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [modalRejecting, setModalRejecting] = useState(false);
  const { toasts, push: toast, dismiss: dismissToast } = useToasts();
  // Модерациядан кейін тізімді қайта жүктеу (setPage(page) React-те ештеңе
  // өзгертпейтін — жарнама экранда қала беретін). silent — спиннерсіз.
  const [reloadKey, setReloadKey] = useState(0);
  const silentReload = useRef(false);

  const [selectedAdId, setSelectedAdId] = useState(null);
  const [modalAdData, setModalAdData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);

  const [selectedAds, setSelectedAds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  // «1181» / «#1181» — ID бойынша іздеу қойындыға қарамай барлық статустардан жүреді
  const idSearch = ID_QUERY_RE.test(debouncedSearchQuery);
  const allStatuses = showAll || idSearch;
  const [sortBy, setSortBy] = useState("newest");
  const [dateFilter, setDateFilter] = useState(null);
  const [photosFilter, setPhotosFilter] = useState(null); // null = все, true = с фото, false = без фото

  const getCategoryName = useCallback((categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? (cat.name_kz || cat.name_ru || cat.name) : "Неизвестно";
  }, [categories]);

  const getSubcategoryName = useCallback((categoryId, subcategoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (cat?.subcategories) {
      const sub = cat.subcategories.find(s => s.id === subcategoryId);
      return sub ? (sub.name_kz || sub.name_ru || sub.name) : "Неизвестно";
    }
    return "Неизвестно";
  }, [categories]);

  const listFilters = useMemo(() => ({
    status: allStatuses ? "all" : status,
    category: selectedCategory,
    subcategory: selectedSubcategory,
    region: selectedRegion,
    search: debouncedSearchQuery,
    sortBy,
    photos: photosFilter,
    date: dateFilter,
  }), [status, allStatuses, selectedCategory, selectedSubcategory, selectedRegion, debouncedSearchQuery, sortBy, photosFilter, dateFilter]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const silent = silentReload.current;
      silentReload.current = false;
      if (!silent) setLoading(true);
      try {
        const params = buildListParams(listFilters, page, ITEMS_PER_PAGE);

        const data = await getAnnouncements(params.toString());
        const parsed = parseAnnouncementsListResponse(data, ITEMS_PER_PAGE, page);

        // Соңғы жарнама модерацияланып бет босаса — алдыңғы бетке.
        if (parsed.items.length === 0 && page > 1) {
          silentReload.current = silent;
          setPage(page - 1);
          return;
        }
        setAnnouncements(parsed.items);
        setTotalPages(parsed.totalPages);
        setTotalItems(parsed.totalItems);
      } catch (err) {
        console.error("Ошибка при загрузке объявлений:", err);
        setAnnouncements([]);
        setTotalPages(1);
        setTotalItems(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, [listFilters, page, reloadKey]);

  useEffect(() => {
    setSelectedSubcategory(null);
  }, [selectedCategory]);

  // Debounce search query - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    getCategories().then(setCategories).catch(console.error);
  }, []);

  useEffect(() => {
    getRegionsPublic()
      .then(data => {
        const regionsArray = Array.isArray(data) ? data : data.regions || [];
        setRegions(regionsArray);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedAdId) return;

    const fetchAd = async () => {
      setModalLoading(true);
      try {
        let data;
        try {
          data = await getAdminAnnouncementById(selectedAdId);
        } catch (adminErr) {
          const is404or405 = adminErr?.message?.includes("405") || adminErr?.message?.includes("404");
          if (is404or405) {
            data = await getAnnouncementByIdPublic(selectedAdId);
          } else {
            throw adminErr;
          }
        }
        setModalAdData(data?.announcement ?? data);
      } catch (err) {
        console.error("Ошибка при загрузке деталей объявления:", err);
        setModalAdData(null);
      } finally {
        setModalLoading(false);
      }
    };
    fetchAd();
  }, [selectedAdId]);

  const handleSaveEdit = async (adId, updatedData) => {
    setEditLoading(true);
    try {
      await editAnnoucmentByID(adId, updatedData);
      setModalAdData(prev => ({ ...prev, ...updatedData }));
      setIsEditing(false);
      // Карточка жаңа мәнмен көрінсін — спиннерсіз қайта жүктеу.
      silentReload.current = true;
      setReloadKey(k => k + 1);
    } catch (err) {
      console.error("Ошибка при сохранении изменений объявления:", err);
      const msg = err.message.includes(":")
        ? err.message.split(":")[1].trim()
        : "Не удалось сохранить изменения.";
      toast(msg, "error");
    } finally {
      setEditLoading(false);
    }
  };

  /** Сәтті модерациядан кейін: бірден экраннан алу (немесе «Барлығы»
   * режимінде статусын жаңарту), сосын спиннерсіз қайта жүктеу. */
  const applyModerated = (ids, newStatus) => {
    if (allStatuses && newStatus) {
      setAnnouncements(prev => prev.map(a => (ids.includes(a.id) ? { ...a, status: newStatus } : a)));
    } else {
      setAnnouncements(prev => prev.filter(a => !ids.includes(a.id)));
      setTotalItems(t => (t == null ? t : Math.max(0, t - ids.length)));
    }
    setSelectedAds(prev => prev.filter(id => !ids.includes(id)));
    setSelectAll(false);
    silentReload.current = true;
    setReloadKey(k => k + 1);
  };

  const setBusy = (ids, on) =>
    setBusyIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  /** Модальда шешім қабылданса — келесі жарнамаға өту (болмаса жабу). */
  const neighbourOf = (id) => {
    const idx = announcements.findIndex(a => a.id === id);
    if (idx < 0) return null;
    return announcements[idx + 1] || (allStatuses ? null : announcements[idx - 1]) || null;
  };

  const moderateOne = async (id, action, newStatus, okText, failText) => {
    if (busyIds.has(id)) return;
    const fromModal = selectedAdId === id;
    const next = fromModal ? neighbourOf(id) : null;
    setBusy([id], true);
    try {
      await action();
      toast(okText);
      applyModerated([id], newStatus);
      if (fromModal) {
        setModalRejecting(false);
        if (next) setSelectedAdId(next.id);
        else closeModal();
      }
    } catch (err) {
      toast(`${failText} #${id}.\n${err.message}`, "error");
    } finally {
      setBusy([id], false);
    }
  };

  const handleAccept = (id) =>
    moderateOne(id, () => acceptAdById(id), "active", `Объявление #${id} принято`, "Не удалось принять объявление");

  const handleReject = (id, reason) =>
    moderateOne(id, () => rejectAdById(id, reason), "rejected", `Объявление #${id} отклонено`, "Не удалось отклонить объявление");

  const openModal = (ad) => {
    setSelectedAdId(ad.id);
    setIsEditing(false);
  };

  function closeModal() {
    setSelectedAdId(null);
    setModalAdData(null);
    setIsEditing(false);
    setModalRejecting(false);
  }

  const openAdjacent = (delta) => {
    const idx = announcements.findIndex(a => a.id === selectedAdId);
    const target = announcements[idx + delta];
    if (target) {
      setModalRejecting(false);
      setIsEditing(false);
      setSelectedAdId(target.id);
    }
  };

  const handleSelectAd = (adId) => {
    setSelectedAds(prev =>
      prev.includes(adId) ? prev.filter(id => id !== adId) : [...prev, adId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAds([]);
    } else {
      setSelectedAds(announcements.map(ad => ad.id));
    }
    setSelectAll(!selectAll);
  };

  /** Сүзгіге сай барлық беттердегі жарнамаларды таңдау (тек ағымдағы 20 емес). */
  const [selectingAll, setSelectingAll] = useState(false);
  const handleSelectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const ids = [];
      for (let p = 1; ; p++) {
        const data = await getAnnouncements(buildListParams(listFilters, p, SELECT_ALL_PAGE_SIZE).toString());
        const parsed = parseAnnouncementsListResponse(data, SELECT_ALL_PAGE_SIZE, p);
        parsed.items.forEach(a => ids.push(a.id));
        if (parsed.items.length < SELECT_ALL_PAGE_SIZE || p >= parsed.totalPages) break;
      }
      setSelectedAds([...new Set(ids)]);
      setSelectAll(true);
    } catch (err) {
      toast(`Не удалось выбрать все объявления\n${err.message}`, "error");
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => {
    setSelectedAds([]);
    setSelectAll(false);
  };

  // Жаппай әрекеттер: қате болса хабарлама және белгіленгендер сақталады.
  const runBulk = async (adIds, action, newStatus, okText, failText) => {
    setBusy(adIds, true);
    try {
      // action() { done, failed } қайтарса — сәттілерін ғана белгілейміз,
      // сәтсіздері белгіленген күйде қалады.
      const result = await action();
      const done = result?.done ?? adIds;
      const failed = result?.failed ?? [];
      if (done.length) applyModerated(done, newStatus);
      if (failed.length) {
        throw new Error(`Ошибка в ${failed.length} из ${adIds.length} (ID ${failed[0].id}): ${failed[0].error}`);
      }
      toast(okText);
    } catch (err) {
      toast(`${failText}\n${err.message}`, "error");
      throw err;
    } finally {
      setBusy(adIds, false);
    }
  };

  const handleBulkAccept = (adIds) =>
    runBulk(adIds, () => bulkAccept(adIds), "active", `Принято: ${adIds.length}`, "Не удалось принять выбранные объявления");

  const handleBulkReject = (adIds, reason) =>
    runBulk(adIds, () => bulkReject(adIds, reason), "rejected", `Отклонено: ${adIds.length}`, "Не удалось отклонить выбранные объявления");

  const handleBulkDelete = (adIds) =>
    runBulk(adIds, () => bulkDelete(adIds), null, `Удалено: ${adIds.length}`, "Не удалось удалить выбранные объявления");

  // Модальдағы пернелер: A/Ф — принять, R/К — отклонить, ←/→ — листать, Esc — закрыть.
  useEffect(() => {
    if (!selectedAdId || isEditing) return undefined;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const ad = announcements.find(a => a.id === selectedAdId);
      const k = e.key.toLowerCase();
      if (e.key === "Escape") {
        if (modalRejecting) setModalRejecting(false);
        else closeModal();
      } else if (modalRejecting) {
        return;
      } else if ((k === "a" || k === "ф") && ad && ad.status !== "active") {
        e.preventDefault();
        handleAccept(selectedAdId);
      } else if ((k === "r" || k === "к") && ad && ad.status !== "rejected") {
        e.preventDefault();
        setModalRejecting(true);
      } else if (e.key === "ArrowRight") {
        openAdjacent(1);
      } else if (e.key === "ArrowLeft") {
        openAdjacent(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const modalIndex = announcements.findIndex(a => a.id === selectedAdId);
  const modalAd = modalIndex >= 0 ? announcements[modalIndex] : null;
  const initialLoading = loading && announcements.length === 0;

  return (
    <div className="space-y-4">
      <ModerationHeader
        status={status}
        totalItems={totalItems}
        totalPages={totalPages}
        page={page}
        announcementsLength={announcements.length}
        selectAll={selectAll}
        onSelectAll={handleSelectAll}
        selectedCount={selectedAds.length}
        onSelectAllMatching={handleSelectAllMatching}
        selectingAll={selectingAll}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        subcategories={selectedCategory ? categories.find(c => c.id === selectedCategory)?.subcategories || [] : []}
        selectedSubcategory={selectedSubcategory}
        onSubcategoryChange={setSelectedSubcategory}
        regions={regions}
        selectedRegion={selectedRegion}
        onRegionChange={setSelectedRegion}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        photosFilter={photosFilter}
        onPhotosFilterChange={setPhotosFilter}
      />

      {idSearch && !showAll && (
        <p className="mb-2 text-xs text-text-secondary">
          Поиск по ID <span className="font-mono text-text-primary">#{debouncedSearchQuery.replace(/\D/g, "")}</span> — по всем статусам объявлений.
        </p>
      )}

      <div className="relative overflow-hidden rounded-xl border border-border-primary bg-bg-card">
        {/* Жүктелу — тізімді жаппай, жоғарыдағы жолақпен (сүзгілер мен іздеу орнында қалады) */}
        {loading && !initialLoading && (
          <div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-emerald-500/20">
            <div className="h-full w-1/3 animate-pulse bg-emerald-500" />
          </div>
        )}

        {initialLoading ? (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="flex animate-pulse items-center gap-4 px-4 py-3">
                <div className="h-4 w-4 rounded bg-bg-tertiary" />
                <div className="h-16 w-24 rounded-lg bg-bg-tertiary" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-1/2 rounded bg-bg-tertiary" />
                  <div className="h-3 w-3/4 rounded bg-bg-tertiary" />
                  <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
                </div>
                <div className="h-9 w-48 rounded-lg bg-bg-tertiary" />
              </li>
            ))}
          </ul>
        ) : announcements.length > 0 ? (
          <ul className={`divide-y divide-border-primary transition-opacity ${loading ? "opacity-60" : ""}`}>
            {announcements.map((ad) => (
              <ModerationRow
                key={ad.id}
                ad={ad}
                selected={selectedAds.includes(ad.id)}
                onToggleSelect={handleSelectAd}
                onOpen={openModal}
                onAccept={handleAccept}
                onReject={handleReject}
                busy={busyIds.has(ad.id)}
                getCategoryName={getCategoryName}
                getSubcategoryName={getSubcategoryName}
              />
            ))}
          </ul>
        ) : (
          <EmptyState showAll={showAll} onRefresh={() => setReloadKey(k => k + 1)} onShowAll={() => setShowAll(true)} />
        )}
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        announcementsLength={announcements.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setPage}
      />

      <BulkModerationActions
        selectedAds={selectedAds.map(id => ({ id }))}
        onBulkAccept={handleBulkAccept}
        onBulkReject={handleBulkReject}
        onBulkDelete={handleBulkDelete}
        onClearSelection={clearSelection}
        isVisible={selectedAds.length > 0}
      />

      {selectedAdId && (
        <Modal onClose={closeModal} maxWidth="6xl">
          {modalLoading || editLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"></div>
                <div className="text-sm text-text-secondary">
                  {editLoading ? "Сохранение изменений..." : "Загрузка деталей объявления..."}
                </div>
              </div>
            </div>
          ) : (
            <AdDetailsModalContent
              adData={modalAdData}
              categories={categories}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              onSave={handleSaveEdit}
            />
          )}
          {!isEditing && (
            <ModerationDetailBar
              ad={modalAd}
              position={modalIndex + 1}
              total={announcements.length}
              onPrev={modalIndex > 0 ? () => openAdjacent(-1) : null}
              onNext={modalIndex >= 0 && modalIndex < announcements.length - 1 ? () => openAdjacent(1) : null}
              onAccept={handleAccept}
              onReject={handleReject}
              rejecting={modalRejecting}
              setRejecting={setModalRejecting}
              busy={busyIds.has(selectedAdId)}
            />
          )}
        </Modal>
      )}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
